import { createTheme } from "@mui/material/styles";
import { COLORS } from "../../constants/colors";
/**
 * utility function for deep merge
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var isObject = function (item) {
    return item && typeof item === "object" && !Array.isArray(item);
};
/**
 * deep merge default theme and custom theme provided by app
 */
var mergeDeep = function (target, source) {
    var output = Object.assign({}, target);
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach(function (key) {
            var _a, _b;
            if (isObject(source[key])) {
                if (!(key in target))
                    Object.assign(output, (_a = {}, _a[key] = source[key], _a));
                else
                    output[key] = mergeDeep(target[key], source[key]);
            }
            else {
                Object.assign(output, (_b = {}, _b[key] = source[key], _b));
            }
        });
    }
    return output;
};
/**
 * default breakpoints
 */
var breakpoints = {
    breakpoints: {
        values: {
            xs: 0,
            sm: 1024,
            md: 1200,
            lg: 1440,
            xl: 1800,
        },
    },
};
/**
 * merges custom breakpoints with default, then provides them to merged theme and custom theme
 * @returns generated theme from default and custom breakpoints and theme
 */
var generateTheme = function (customTheme, customBreakpoints) {
    if (customTheme === void 0) { customTheme = {}; }
    if (customBreakpoints === void 0) { customBreakpoints = {}; }
    var mergedBreakpoints = mergeDeep(breakpoints, customBreakpoints);
    var breakpointTheme = createTheme(mergedBreakpoints);
    var theme = {
        palette: {
            primary: {
                main: COLORS.SAY_COBALT,
                contrastText: COLORS.WHITE,
                dark: COLORS.SAY_COBALT_HOVER,
            },
            secondary: {
                main: COLORS.SECONDARY,
                contrastText: COLORS.SAY_COBALT,
                dark: COLORS.SECONDARY_HOVER,
            },
            error: {
                main: COLORS.ERROR_DARK,
                contrastText: COLORS.WHITE,
                dark: COLORS.ERROR_HOVER,
            },
            delete: {
                main: COLORS.ERROR_DARK,
                contrastText: COLORS.WHITE,
            },
        },
        breakpoints: breakpointTheme.breakpoints,
        typography: {
            fontFamily: ["Inter", "Helvetica Neue", "sans-serif"].join(","),
            h1: {
                fontSize: "3.75rem",
                lineHeight: "4.5rem",
                letterSpacing: "0px",
            },
            h2: {
                fontSize: "3rem",
                lineHeight: "3.5rem",
                letterSpacing: "0px",
            },
            h3: {
                fontSize: "2.25rem",
                lineHeight: "2.25rem",
                fontWeight: 400,
                letterSpacing: "0px",
            },
            h4: {
                fontSize: "1.5rem",
                fontWeight: 600,
                lineHeight: "1.75rem",
                letterSpacing: "0px",
            },
            h5: {
                fontSize: "1.25rem",
                lineHeight: "1.5rem",
                fontWeight: 600,
                letterSpacing: "0",
            },
            subtitle1: {
                fontSize: "1rem",
                lineHeight: "1.5rem",
                fontWeight: 600,
                letterSpacing: "0.15px",
            },
            subtitle2: {
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
                fontWeight: 600,
                letterSpacing: "0px",
            },
            subtitle3: {
                fontSize: "0.75rem",
                lineHeight: "1rem",
                fontWeight: 600,
                letterSpacing: "0px",
                fontFamily: ["Inter", "Helvetica Neue", "sans-serif"].join(","),
            },
            body1: {
                fontSize: "1rem",
                lineHeight: "1.5rem",
                letterSpacing: "0px",
            },
            body2: {
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
                letterSpacing: "0px",
            },
            body3: {
                fontSize: "0.75rem",
                lineHeight: "1.125rem",
                letterSpacing: "0.2px",
                fontFamily: ["Inter", "Helvetica Neue", "sans-serif"].join(","),
            },
            caption: {
                fontSize: "0.625rem",
                lineHeight: "1rem",
                letterSpacing: "0.3px",
            },
            overline: {
                fontSize: "0.625rem",
                lineHeight: "1rem",
                letterSpacing: "1.25px",
                fontWeight: 600,
            },
        },
        spacing: 4,
        components: {
            MuiTypography: {
                defaultProps: {
                    variantMapping: {
                        body3: "p",
                    },
                },
            },
            MuiTouchRipple: {
                styleOverrides: {
                    root: {
                        color: COLORS.SAY_COBALT,
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: "\n         * {\n           box-sizing: border-box;\n         }\n         b {\n           font-weight: 600;\n         }\n         strong, b {\n           font-weight: 600;\n         }\n         li.public-DraftStyleDefault-orderedListItem {\n            list-style-type: decimal;\n          }\n          li.public-DraftStyleDefault-orderedListItem:before {\n          content: none;\n          }",
            },
            /*MuiOutlinedInput: { // deprecated in material v5 TBD: go add 12.5 in a different manner
              styleOverrides: {
                inputMarginDense: {
                  paddingTop: "12.5px",
                  paddingBottom: "12.5px",
                },
              },
            },*/
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        fontFamily: ["Inter", "Helvetica Neue", "sans-serif"].join(","),
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    containedPrimary: {
                        color: COLORS.WHITE,
                        backgroundColor: COLORS.SAY_COBALT,
                    },
                    outlinedPrimary: {
                        color: COLORS.BLACK,
                    },
                    textPrimary: {
                        color: COLORS.BLACK,
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        marginTop: "8px",
                        marginBottom: "8px",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: { borderRadius: 8 },
                    // Design System Elevations
                    elevation1: {
                        boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12), 0px 1px 3px rgba(0, 0, 0, 0.2)",
                    },
                    elevation2: {
                        boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px rgba(0, 0, 0, 0.12), 0px 1px 5px rgba(0, 0, 0, 0.2)",
                    },
                    elevation3: {
                        boxShadow: "0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.2)",
                    },
                    elevation4: {
                        boxShadow: "0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.2)",
                    },
                    elevation6: {
                        boxShadow: "0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12), 0px 3px 5px rgba(0, 0, 0, 0.2)",
                    },
                    elevation9: {
                        boxShadow: "0px 9px 12px rgba(0, 0, 0, 0.14), 0px 3px 16px rgba(0, 0, 0, 0.12), 0px 5px 6px rgba(0, 0, 0, 0.2)",
                    },
                    elevation12: {
                        boxShadow: "0px 12px 17px rgba(0, 0, 0, 0.14), 0px 5px 22px rgba(0, 0, 0, 0.12), 0px 7px 8px rgba(0, 0, 0, 0.2)",
                    },
                    elevation16: {
                        boxShadow: "0px 16px 24px rgba(0, 0, 0, 0.14), 0px 6px 30px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.2)",
                    },
                    elevation24: {
                        boxShadow: "0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2)",
                    },
                    // custom elevation used for card surfaces
                    elevation5: {
                        boxShadow: "0px -8px 24px rgba(0, 0, 0, 0.02), 0px 8px 24px rgba(0, 0, 0, 0.04)",
                    },
                    // custom elevation used for dropdowns; overwriting elevation 8
                    // which is the default for many Paper/dropdown components
                    elevation8: {
                        boxShadow: "0px -12px 24px rgba(0, 0, 0, 0.02), 0px 12px 24px rgba(0, 0, 0, 0.06)",
                    },
                },
            },
        },
    };
    var mergedTheme = mergeDeep(theme, customTheme);
    return createTheme(mergedTheme);
};
export default generateTheme;
